import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelectState } from "./hooks/useSelectState";
import { useSelectKeyboard } from "./hooks/useSelectKeyboard";
import { useSelectValidation } from "./hooks/useSelectValidation";
import type { SelectOption, FormSelectProps } from "./Select.types";
import { useSelectRefs } from "./Select.helpers";
import SelectInputMode from "./Select.input-mode";
import SelectTriggerMode from "./Select.trigger-mode";

export type { SelectOption } from "./Select.types";
export type { FormSelectProps } from "./Select.types";

const FormSelect: React.FC<FormSelectProps> = (props) => {
  const {
    name = '', onChange: rawOnChange = () => {}, value: rawValue, children: rawChildren, label: rawLabel,
    data: rawData, options: rawOptions, placeholder: rawPlaceholder,
    multiple = false,
    searchable: rawSearchable, clearable = false, allowDeselect = false, disabled = false,
    immediateValidation = true, errors: rawErrors = new Set<string>(), error,
    required = false, submitted = false, toPresentErrors = true, autofocus = false,
    filterFunction, maxResults = 50, enableEnterKeySelection = true, enableAutoSelection = false,
    renderOption, renderEmptyState, getDisplayNameForValue: customGetDisplayName,
    nothingFoundMessage, leftSection, rightSection,
    onNextField, onSubmit: onSubmitProp, onBlur: onBlurProp,
    id: idProp, style: styleProp, className: classNameProp,
    ...restProps
  } = props;

  const searchable = rawSearchable ?? (rawData !== undefined ? false : true);
  const useTriggerMode = rawData !== undefined;
  const labelText = (typeof rawChildren === 'string' ? rawChildren : rawLabel) || '';
  const onChange = rawOnChange;
  const singleValue = Array.isArray(rawValue) ? '' : (rawValue ?? '');
  const errors = rawErrors;
  const multipleValues = Array.isArray(rawValue)
    ? (rawValue as string[])
    : (multiple && typeof rawValue === 'string' && rawValue ? [rawValue] : []);

  const normalizedData: SelectOption[] = useMemo(() => {
    const src = rawData ?? rawOptions ?? [];
    return src.map((item) => typeof item === 'string' ? { value: item, label: item } : item);
  }, [rawData, rawOptions]);

  const optionValues = useMemo(() => normalizedData.map(d => d.value), [normalizedData]);

  const getDisplayNameForValue = useCallback((v: string) => {
    if (customGetDisplayName) return customGetDisplayName(v);
    const found = normalizedData.find(d => d.value === v);
    return found ? found.label : v;
  }, [customGetDisplayName, normalizedData]);

  const { t } = useTranslation('common');
  const refs = useSelectRefs();
  const requiredErrorMessage = t('form.validation.required', { field: labelText });

  const multipleSummary = useCallback(
    (count: number) => t('form.messages.itemsSelected', { count }),
    [t],
  );

  const state = useSelectState({
    value: singleValue, options: optionValues, normalizedData, getDisplayNameForValue,
    filterFunction, maxResults, enableAutoSelection, searchable, clearable, allowDeselect,
    onChange, name, useTriggerMode,
    multiple, multipleValues, multipleSummary,
  });

  const { handleKeyDown, handleSearchKeyDown, resetKeyboardNavigation } = useSelectKeyboard({
    name, enableEnterKeySelection, isFocused: state.isFocused, filteredOptions: state.filteredOptions,
    filteredData: state.filteredData, activeOptionIndex: state.activeOptionIndex,
    setActiveOptionIndex: state.setActiveOptionIndex, selectedValue: state.selectedValue,
    query: state.query, onSelect: state.onSelect, ensureDisplayNameInQuery: state.ensureDisplayNameInQuery,
    onNextField, onSubmit: onSubmitProp, searchable, isOpen: state.isOpen, close: state.close,
  });

  const { allErrorMessages, hasErrorBorder } = useSelectValidation({
    submitted, selectedValue: state.selectedValue, required, requiredErrorMessage, errors, error,
  });

  if (!useTriggerMode) {
    return (
      <SelectInputMode
        state={state} refs={refs} name={name} labelText={labelText} required={required}
        autofocus={autofocus} immediateValidation={immediateValidation} errors={errors}
        submitted={submitted} toPresentErrors={toPresentErrors} allErrorMessages={allErrorMessages}
        classNameProp={classNameProp} styleProp={styleProp} getDisplayNameForValue={getDisplayNameForValue}
        renderOption={renderOption} renderEmptyState={renderEmptyState} nothingFoundMessage={nothingFoundMessage}
        t={t} handleKeyDown={handleKeyDown} resetKeyboardNavigation={resetKeyboardNavigation}
      />
    );
  }

  return (
    <SelectTriggerMode
      state={state} refs={refs} name={name} labelText={labelText} required={required} disabled={disabled}
      multiple={multiple} searchable={searchable} rawSearchable={rawSearchable} clearable={clearable}
      rawValue={typeof rawValue === 'string' ? rawValue : null} rawPlaceholder={rawPlaceholder} singleValue={singleValue}
      leftSection={leftSection} idProp={idProp} classNameProp={classNameProp} styleProp={styleProp}
      restProps={restProps} submitted={submitted} toPresentErrors={toPresentErrors}
      allErrorMessages={allErrorMessages} hasErrorBorder={hasErrorBorder} renderOption={renderOption}
      nothingFoundMessage={nothingFoundMessage} handleSearchKeyDown={handleSearchKeyDown} t={t}
    />
  );
};

export { FormSelect as Select };
export default FormSelect;
